/**
 * Chat Actions Hook
 *
 * Provides methods for interacting with the rule build AI chat.
 * Handles message sending, interrupt responses, and stream control.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ResumePayload } from '@features/ai-chat-core';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  addUserMessage,
  clearError,
  initializeChat,
  resetChat,
  type RuleBuildChatRequest,
  type RuleBuildChatState,
  setFocusedDraftId,
  useStreamRuleBuildChatQuery,
} from '../store';

// ============================================================================
// Types
// ============================================================================

export interface UseChatActionsOptions {
  /** Callback when a stream ends (message fully received) */
  onStreamEnd?: () => void;
}

export interface UseChatActionsResult {
  /** Send a new message to the AI */
  sendMessage: (content: string) => void;
  /** Resume from an interrupt with the given payload */
  resumeInterrupt: (payload: ResumePayload) => void;
  /** Cancel the current stream */
  cancelStream: () => void;
  /** Initialize chat for a task */
  initialize: () => void;
  /** Reset chat state */
  reset: () => void;
  /** Set the focused draft for context */
  setFocusedDraft: (draftId: string | null) => void;
  /** Clear any error */
  clearChatError: () => void;
  // State
  /** Whether a stream is currently active */
  isStreaming: boolean;
  /** Whether an interrupt is active */
  isInterrupted: boolean;
  /** Whether the assistant is thinking/processing */
  isThinking: boolean;
  /** Whether the chat is ready for input */
  isReady: boolean;
  /** Current thread ID */
  threadId: string | null;
  /** Current error message */
  error: string | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for managing rule build chat interactions
 *
 * @param taskId - The current task ID
 * @param enabled - Whether chat should be enabled
 * @param options - Optional callbacks
 * @returns Chat action methods and state
 */
export const useChatActions = (
  taskId: string | null,
  enabled: boolean = true,
  options: UseChatActionsOptions = {},
): UseChatActionsResult => {
  const dispatch = useAppDispatch();
  const { onStreamEnd } = options;

  // Store callback in ref to avoid stale closures
  const onStreamEndRef = useRef(onStreamEnd);
  useEffect(() => {
    onStreamEndRef.current = onStreamEnd;
  }, [onStreamEnd]);

  // Select chat state from dynamic feature state
  const chatState = useAppSelector(
    (state) => (state as unknown as { 'ruleBuild/chat'?: RuleBuildChatState })['ruleBuild/chat']
  );

  // Extract state values with defaults
  const threadId = chatState?.threadId ?? null;
  const interrupt = chatState?.interrupt ?? null;
  const assistantStatus = chatState?.assistant?.status ?? 'idle';
  const focusedDraftId = chatState?.focusedDraftId ?? null;
  const error = chatState?.error ?? null;

  // Compute derived state
  const isStreaming = assistantStatus === 'streaming';
  const isThinking = assistantStatus === 'thinking';
  const isInterrupted = interrupt?.isActive ?? false;
  const isReady = enabled && taskId !== null && !isStreaming && !isThinking;

  // Track previous streaming state to detect stream end
  const wasStreamingRef = useRef(false);

  // Detect when streaming ends
  useEffect(() => {
    // If we were streaming and now we're not, the stream has ended
    if (wasStreamingRef.current && !isStreaming && !isThinking) {
      console.log('[useChatActions] Stream ended, calling onStreamEnd');
      onStreamEndRef.current?.();
    }
    wasStreamingRef.current = isStreaming || isThinking;
  }, [isStreaming, isThinking]);

  // Generate unique run ID for each request
  const generateRunId = useCallback(() => crypto.randomUUID(), []);

  /**
   * Create the request payload for streaming
   */
  const createRequestPayload = useCallback(
    (
      options:
        | { type: 'message'; content: string }
        | { type: 'resume'; payload: ResumePayload },
    ): RuleBuildChatRequest | null => {
      if (!taskId) return null;

      const basePayload: RuleBuildChatRequest = {
        taskId,
        runId: generateRunId(),
        threadId: threadId || undefined,
        focusedRuleId: focusedDraftId || undefined,
      };

      if (options.type === 'message') {
        return {
          ...basePayload,
          messages: [
            {
              id: `user-${Date.now()}`,
              role: 'user',
              content: options.content,
            },
          ],
        };
      } else {
        if (!interrupt?.interruptId) return null;
        return {
          ...basePayload,
          resume: {
            interruptId: interrupt.interruptId,
            payload: options.payload,
          },
        };
      }
    },
    [taskId, threadId, focusedDraftId, interrupt?.interruptId, generateRunId],
  );

  // Track current stream request
  const [currentStreamKey, setCurrentStreamKey] = useState<string | null>(null);
  const [streamRequest, setStreamRequest] = useState<RuleBuildChatRequest | null>(null);

  // Use the streaming query (skip if no request)
  useStreamRuleBuildChatQuery(streamRequest!, {
    skip: !streamRequest,
  });

  /**
   * Send a new message to the AI
   */
  const sendMessage = useCallback(
    (content: string) => {
      if (!isReady || !content.trim()) return;

      // Add user message to state
      dispatch(addUserMessage(content.trim()));

      // Create and trigger stream request
      const payload = createRequestPayload({ type: 'message', content: content.trim() });
      if (payload) {
        setStreamRequest(payload);
        setCurrentStreamKey(`msg-${Date.now()}`);
      }
    },
    [isReady, dispatch, createRequestPayload],
  );

  /**
   * Resume from an interrupt
   */
  const resumeInterrupt = useCallback(
    (payload: ResumePayload) => {
      if (!taskId || !interrupt?.isActive) return;

      const request = createRequestPayload({ type: 'resume', payload });
      if (request) {
        setStreamRequest(request);
        setCurrentStreamKey(`resume-${Date.now()}`);
      }
    },
    [taskId, interrupt?.isActive, createRequestPayload],
  );

  /**
   * Cancel the current stream
   */
  const cancelStream = useCallback(() => {
    // Clear stream request to stop the query
    setStreamRequest(null);
    setCurrentStreamKey(null);
  }, []);

  /**
   * Initialize chat for a task
   */
  const initialize = useCallback(() => {
    if (taskId) {
      dispatch(initializeChat({ taskId }));
    }
  }, [dispatch, taskId]);

  /**
   * Reset chat state
   */
  const reset = useCallback(() => {
    dispatch(resetChat());
    setStreamRequest(null);
    setCurrentStreamKey(null);
  }, [dispatch]);

  /**
   * Set focused draft for context
   */
  const setFocusedDraft = useCallback(
    (draftId: string | null) => {
      dispatch(setFocusedDraftId(draftId));
    },
    [dispatch],
  );

  /**
   * Clear error state
   */
  const clearChatError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    sendMessage,
    resumeInterrupt,
    cancelStream,
    initialize,
    reset,
    setFocusedDraft,
    clearChatError,
    isStreaming,
    isInterrupted,
    isThinking,
    isReady,
    threadId,
    error,
  };
};

export default useChatActions;
